<template>
  <v-container class="mb-8">
    <!-- Home > Store -->
    <v-breadcrumbs
      class="pl-1 py-2"
      divider=">"
      :items="breadcrumb"
    ></v-breadcrumbs>
    <v-row align="center" no-gutters style="height: 40px">
      <!-- Back Button -->
      <v-btn text small class="px-1" @click="$router.go(-1)"
        ><v-icon left small> mdi-chevron-left </v-icon
        >{{ $langs.Gen.goBack[$langs.lang] }}</v-btn
      >
    </v-row>

    <v-row no-gutters class="align-center">
      <h1 class="text-h6 primary--text">{{ store.storeName }}</h1>
      <div class="ml-auto">
        <!-- Report Button -->
        <v-btn
          v-if="isLoggedIn && myStoreInfo._id != store._id"
          small
          text
          color="secondary"
          @click="reportStore"
          :ripple="false"
          ><v-icon small left>flag</v-icon
          >{{ $langs.Gen.report[$langs.lang] }}</v-btn
        >
        <!-- Share Button -->
        <v-btn small outlined class="ml-1" :ripple="false" @click="shareLink">
          <v-icon small left>mdi-share-variant</v-icon>
          Share
        </v-btn>
      </div>
    </v-row>

    <v-card class="mt-4 py-6 px-6">
      <v-row>
        <v-col cols="12" class="logo-img mx-auto">
          <v-img
            class="mx-auto align-center text-center"
            max-width="200"
            :aspect-ratio="1.05"
            :src="store.logoImage"
          >
            <v-icon v-if="store._id && !store.logoImage" large color="grey"
              >mdi-image-off-outline</v-icon
            >
          </v-img>
        </v-col>

        <v-col class="text-subtitle-2 d-flex flex-column py-2">
          <v-btn
            v-if="!isLoading && isLoggedIn && myStoreInfo._id != store._id"
            absolute
            right
            top
            small
            icon
            color="primary"
            @click="followStore"
            :ripple="false"
            :disabled="isUpdatingFollow"
            ><v-icon :small="$vuetify.breakpoint.xsOnly">
              {{ isFollowing ? "mdi-heart" : "mdi-heart-outline" }}
            </v-icon></v-btn
          >

          <div>
            {{ $langs.MyStore.owner[$langs.lang] }} :
            {{ store.user.name.firstname }}
            {{ store.user.name.lastname }}
          </div>

          <!-- Address -->
          <div class="mb-1" v-if="store.address">
            {{ $langs.MyStore.storeLocation[$langs.lang] }} :
            <div
              class="grey--text text--darken-3 d-flex flex-wrap"
              style="max-width: 400px; flex-basis: 400px"
            >
              <span class="mr-1" v-if="store.address.no"
                >เลขที่ {{ store.address.no }}</span
              >
              <span class="mr-1" v-if="store.address.moo"
                >หมู่ {{ store.address.moo }}</span
              >
              <span class="mr-1" v-if="store.address.soi"
                >ซอย {{ store.address.soi }}</span
              >
              <span class="mr-1" v-if="store.address.building"
                >หมู่บ้าน/อาคาร {{ store.address.building }}</span
              >
              <span class="mr-1" v-if="store.address.road"
                >ถนน {{ store.address.road }}</span
              >
              <span class="mr-1" v-if="store.address.subdistrict"
                >ตำบล {{ store.address.subdistrict }}</span
              >
              <span class="mr-1" v-if="store.address.district"
                >อำเภอ {{ store.address.district }}</span
              >
              <span class="mr-1" v-if="store.address.province"
                >จังหวัด {{ store.address.province }}
                {{ store.address.zipCode }}</span
              >
            </div>
          </div>

          <!-- Contact -->
          <template v-for="(v, k) in store.contact">
            <div v-if="v" :key="k" class="d-flex">
              <span class="mr-1" style="white-space: nowrap"
                >{{ contact.title[k] }} :</span
              >
              <a
                v-if="isLink(k, v)"
                class="text-truncate d-inline-block"
                :href="genLinkHref(k, v)"
                :target="k != 'tel' && k != 'email' && '_blank'"
                >{{ v.replace("https://", "").replace("http://", "") }}</a
              >
              <span v-else>{{ v }}</span>
            </div>
          </template>

          <!-- Join Date -->
          <div class="text-caption text-right mt-auto">
            {{
              isLoading
                ? $langs.Gen.loading[$langs.lang]
                : `${$langs.MyStore.joined[$langs.lang]} ${new Date(
                    store.createdAt
                  ).toLocaleDateString($langs.lang)}`
            }}
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- About Store -->
    <v-card class="mt-4 py-4 px-6 text-body-2" v-if="store.description">
      <div class="font-weight-medium">
        {{ $langs.MyStore.aboutStore[$langs.lang] }}
      </div>
      <div v-html="store.description"><!-- Render with .innerHTML --></div>
    </v-card>

    <!-- PRODUCT TITLE -->
    <span id="products"></span>
    <v-row v-if="!!products.length" class="mt-6 px-4" align="center" no-gutters>
      <v-divider class="bold-1"></v-divider>
      <span class="text-h6 px-4 text-capitalize">{{
        $langs.Gen.product[$langs.lang]
      }}</span>
      <v-divider class="bold-1"></v-divider>
    </v-row>

    <!-- Item list -->
    <div
      class="list-item fade-in"
      v-for="product in products"
      :key="product._id"
    >
      <Item
        class="d-block mx-auto"
        :product="product"
        :type="product.type"
      ></Item>
    </div>

    <!-- Page view -->
    <v-pagination
      class="my-4"
      v-if="!!productCount"
      v-model="PAGE"
      :length="pageCount"
      :total-visible="visiblePagination[$vuetify.breakpoint.name]"
      @input="goPage"
    ></v-pagination>

    <!-- To ReptTown (if no item) -->
    <div class="mt-8 text-center">
      <v-btn
        outlined
        color="primary"
        :href="`${appRPTUrl}/stores/s/${$route.params.id}`"
        :ripple="false"
        >ดูสินค้าเพิ่มเติมที่ ReptTown</v-btn
      >
    </div>
  </v-container>
</template>

<script>
import ItemList from "@/components/base/ItemList";
import Item from "@/components/base/Item";

import { storeImageUrl, appRPTUrl } from "../services/constants";
import api from "../services/api";
import user from "../services/user";

export default {
  name: "StoreView",
  props: ["cacheData"],
  components: {
    ItemList,
    Item,
  },
  data() {
    return {
      store: {
        contact: {},
        user: { name: {} },
        storeName: "",
        address: { province: "" },
        logoImage: "",
        description: "",
        createdAt: "",
      },
      ITEM_PRE_PAGE: 60,
      products: [],
      productCount: 0,
      pageCount: 0,
      PAGE: 1,
      visiblePagination: { xs: 4, sm: 6, md: 9, lg: 11 },
      breadcrumb: [
        { text: this.$langs.Gen.home[this.$langs.lang], to: "/" },
        {
          text: this.$langs.Gen.stores[this.$langs.lang],
          to: "/stores",
          exact: true,
        },
        { text: "", to: this.$route.path },
      ],
      contact: {
        title: {
          tel: "Telephone",
          fb: "Facebook",
          web: "Website",
          line: "Line",
          email: "Email",
        },
        icon: {
          tel: "mdi-cellphone",
          fb: "mdi-facebook",
          web: "mdi-web",
          line: "Line",
          email: "mdi-email",
        },
      },
      isLoading: true,
      isUpdatingFollow: false,

      appRPTUrl: appRPTUrl,
    };
  },
  mounted() {
    if (this.cacheData) {
      this.store = { ...this.store, ...this.cacheData };
      this.breadcrumb[2].text = this.store.storeName;
    }
    this.PAGE = parseInt(this.$route.query.page) || 1;
    this.getStoreInfo();
    this.getProducts();
  },
  methods: {
    async getStoreInfo() {
      let id = this.$route.params.id;
      let { admin, store: query_store } = this.$route.query;
      let { data: store, status } = await api.getStoreInfo(id, {
        admin,
        store: query_store,
      });
      this.isLoading = false;
      if (status == 404) return this.$router.replace("/notfound");
      if (!store) return;
      if (store.description)
        store.description = store.description
          .replace(/</g, "&#60;")
          .replace(/\n/g, "<br>")
          .replace(/\s\s/g, "&ensp;");
      this.store = store;
      this.store.logoImage = storeImageUrl(store.logoImage);
      this.breadcrumb[2].text = store.storeName;
    },
    async getProducts() {
      let id = this.$route.params.id;
      let { products, amount } = await api.getProductsByStore(id, {
        count: this.productCount == 0,
        page: this.PAGE,
        size: this.ITEM_PRE_PAGE,
      });
      if (amount) {
        this.productCount = amount;
        this.pageCount = Math.ceil(amount / this.ITEM_PRE_PAGE);
      }
      this.products = products;
    },
    async reportStore() {
      let message = prompt("รายละเอียด");
      if (!message) return;
      const { success, msg } = await user.reportStore(this.store._id, message);
      if (!success) return api.makeAlert(msg, "error");
      api.makeAlert("Your report is sent.", "primary");
    },
    isLink(key, web) {
      if (key == "line" && !web.includes("line.me")) return false;
      if (key == "tel" || key == "email") return true;
      return /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi.test(
        web
      );
    },
    genLinkHref(key, v) {
      if (key == "tel") return `tel:${v.replace(/-/g, "")}`;
      if (key == "email") return `mailto:${v}`;
      return `${v.indexOf("http") == -1 ? "http://" : ""}${v}`;
    },
    async followStore() {
      this.isUpdatingFollow = true;
      if (this.store.isFollowed) {
        let result = await user.followStore(this.store._id, false);
        if (result.success) this.store.isFollowed = false;
      } else {
        let result = await user.followStore(this.store._id);
        console.log(result);
        if (result.success) this.store.isFollowed = true;
        else api.makeAlert(result.msg, "info");
      }
      this.isUpdatingFollow = false;
    },
    shareLink() {
      navigator.share({
        title: this.store.storeName,
        url: this.$route.path,
      });
    },
    goPage(page) {
      if (page == 1) page = undefined;
      this.$router.push({ query: { ...this.$route.query, page } });
      for (let i = 0; i < this.products.length; i++)
        this.products[i] = { images: [" "] };
      this.$vuetify.goTo("#products", { duration: 0 });
    },
  },
  watch: {
    "$route.query.page": function (page) {
      this.PAGE = parseInt(page) || 1;
      this.getProducts();
    },
  },
  computed: {
    isLoggedIn() {
      return !!user.restore().userInfo;
    },
    myStoreInfo() {
      return user.restore().storeInfo || {};
    },
    isFollowing() {
      return this.store.isFollowed;
    },
  },
  metaInfo() {
    return {
      title: this.store.storeName || "Store",
      titleTemplate: "%s · Marketpett",
      // meta: [
      //   {
      //     vmid: "description",
      //     name: "description",
      //     content: "Marketpett",
      //   },
      // ],
    };
  },
};
</script>

<style lang="scss" scoped>
.list-item {
  // background: red;
  display: inline-block;

  @media only screen and (min-width: 100px) {
    width: 50%;
  }

  @media only screen and (min-width: 475px) {
    width: 33%;
  }

  @media #{map-get($display-breakpoints, 'sm-and-up' )} {
    // background: green;
    width: 33%;
  }

  @media only screen and (min-width: map-get($grid-breakpoints, "sm" )) and (max-width: 700px) {
  }

  @media #{map-get($display-breakpoints, 'md-and-up' )} {
    // background: blue;
    width: 25%;
  }
  @media #{map-get($display-breakpoints, 'lg-and-up' )} {
    // background: chartreuse;
    width: 20%;
  }
}

.logo-img {
  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    align-self: center;
    max-width: 210px;
    flex-basis: 210px;
  }
}
</style>